import { useCallback, useEffect, useRef, useState } from 'react'
import {
  createAutoAgentSession, getActiveAgentSession, getAutoAgentTranscript,
  type AutoAgentTranscriptEntry,
} from '@/api/autoagent'
import { WS_BASE } from '@/config/basePath'
import { useAgentStore } from '@/store/agentStore'
import type { WireAttachment } from '@/lib/attachments'
import { refreshCredits } from '@/store/creditsStore'
import { takePendingResend } from '@/store/aiAccessStore'
import { upsertAskReply, type AutoAgentAskRecord } from '@/lib/askRecords'

export type { AutoAgentAskRecord }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// awaiting_review = the agent stopped at a phase boundary (the prototype
// review or the end of a phase) and is waiting for the user's
// Continue / Build task by task / Complete-everything decision.
export type AgentStatus = 'setup' | 'running' | 'awaiting_review' | 'paused' | 'completed' | 'error'
export type TodoStatus = 'pending' | 'running' | 'completed' | 'skipped' | 'error'
export type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error'
export type RunScope = 'phase' | 'task' | 'all'

export interface AutoAgentTodo {
  id: string
  step: string
  label: string
  status: TodoStatus
  error?: string | null
  obj_name?: string | null
  obj_type?: string | null
  started_at?: number | null
  completed_at?: number | null
  phase_id?: string | null
  review_iters?: number | null
  // Furthest completed sub-step within this object's build loop:
  // 'model_done' -> 'bl_done' (T objects) -> (review) -> completed.
  stage?: string | null
  // Per-object resource usage accumulated during build (LLM + CLI agent calls).
  input_tokens?: number | null
  output_tokens?: number | null
  cost_usd?: number | null
}

// An agent-decided logical build phase (the "next logical stop").
export interface AutoAgentPhase {
  id: string
  title: string
  rationale?: string
  objects: string[]
}

export interface AutoAgentLogEntry {
  id: number
  message: string
  // 'success' is a persistent milestone line (e.g. a sub-step checkpoint or a
  // passing review) — like warning/error it stays in chat history, whereas
  // 'info' is transient progress chatter (only the latest one is shown).
  level: 'info' | 'success' | 'warning' | 'error'
  timestamp: Date
  // Per-milestone accounting shown as right-aligned chips ("1m 23s · 12.4k
  // tokens · $0.21"); present only on success-level checkpoints.
  elapsedS?: number | null
  tokens?: number | null
  costUsd?: number | null
}

export interface AutoAgentQuestion {
  question_id: string
  question: string
  options: string[]
  // Rich scope-confirmation payload (pre-FRS build round). When present the
  // UI renders the ScopeConfirmation component instead of plain options and
  // answers with a JSON ScopeDecisions string.
  scope?: import('@/types').ScopeProposal
  // Large-source table inventory (the exploration scoping round). When
  // present the UI renders the SourceTablePicker component and answers with
  // a JSON {"tables": [...]} string.
  table_picker?: import('@/types').SourceTablePickerPayload
  // The "I will provide the connection" option should open the
  // connection-definition form; the answer names the saved connection's id.
  connection_form?: boolean
  // A secret-collecting form the question offers — 'ioflow_connect' opens the
  // IOFlow connect + test form for `app_id` (the answer names a subscription).
  form_kind?: 'ioflow_connect'
  app_id?: string
  // Nothing on the server is waiting on this question any more (the backend
  // restarted while it was open), but it is still owed: answering it runs the
  // step that asked it again, which takes the answer without asking.
  parked?: boolean
  // When it was asked (epoch seconds).
  asked_at?: number
}

export interface AutoAgentReply {
  id: number
  content: string
  role: 'user' | 'agent'
  timestamp: Date
  // Set on the agent reply that records a question (see AutoAgentAskRecord).
  ask?: AutoAgentAskRecord
  // Resource usage for an agent reply (the Ask-AI turn that produced it).
  durationMs?: number | null
  inputTokens?: number | null
  outputTokens?: number | null
  costUsd?: number | null
}

export interface TaskInfo {
  task_type: string
  effort: string
  model: string
}

// Resource summary for one completed Ask-AI turn (time + tokens across every
// model + CLI-agent call the turn made, nested object builds included).
export interface AutoAgentTurnUsage {
  id: number
  durationMs: number
  inputTokens: number
  outputTokens: number
  costUsd?: number | null
  calls: number
  timestamp: Date
}

// One plan as summarised by the backend `plans` event (the dock switcher list).
export interface AutoAgentPlanSummary {
  id: string
  title: string
  kind: string
  status: AgentStatus
  n_done: number
  n_total: number
  created_at: number
  updated_at: number
  depends_on?: string[]
}

interface WsEvent {
  type: string
  // Per-session event counter (multi-device fan-out). Used to drop events a
  // reconnect replays twice and to ask for only what was missed.
  seq?: number
  // user_message / answer_given: which client sent it, so the sender skips
  // its own echo (it already shows that bubble).
  client_id?: string | null
  answer?: string | null
  kind?: string
  attachments?: string[]
  todos?: AutoAgentTodo[]
  status?: AgentStatus
  todo_id?: string
  error?: string | null
  message?: string
  level?: 'info' | 'success' | 'warning' | 'error'
  question_id?: string
  question?: string
  options?: string[]
  scope?: import('@/types').ScopeProposal
  table_picker?: import('@/types').SourceTablePickerPayload
  connection_form?: boolean
  form_kind?: 'ioflow_connect'
  app_id?: string
  content?: string
  step_id?: string
  task_type?: string
  effort?: string
  model?: string
  phases?: AutoAgentPhase[]
  summary?: string
  changes?: string[]
  review_iters?: number | null
  phase_id?: string | null
  stage?: string | null
  // Phase-boundary pause context (why it paused / what's next).
  current_phase?: { id?: string; title?: string; objects?: string[] } | null
  next_phase?: { id?: string; title?: string; count?: number; objects?: string[] } | null
  started_at?: number | null
  completed_at?: number | null
  input_tokens?: number | null
  output_tokens?: number | null
  cost_usd?: number | null
  // Per-milestone accounting on 'log' events (success-level checkpoints).
  elapsed_s?: number | null
  tokens?: number | null
  // Resource summary for an Ask-AI chat reply (time + tokens for that turn).
  duration_ms?: number | null
  calls?: number | null
  // Multi-plan events.
  plans?: AutoAgentPlanSummary[]
  active_plan_id?: string | null
  plan_id?: string | null
  // question: parked across a restart (see AutoAgentQuestion.parked).
  parked?: boolean
  asked_at?: number
  // error: the session is gone for good (close 4004).
  fatal?: boolean
  // ai_access_error: platform allowance exhausted / no usable key.
  code?: string
  detail?: string
  plan?: string | null
  plan_label?: string | null
  used?: number | null
  limit?: number | null
}

// A readable transcript line for a question answer. Scope and table-picker
// answers travel as JSON; show what was chosen, not the raw payload.
export function summarizeAnswer(answer: string | null | undefined): string {
  const text = (answer || '').trim()
  if (!text) return ''
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as { tables?: string[]; answers?: { selected?: unknown[] }[] }
      if (Array.isArray(parsed.tables)) {
        const n = parsed.tables.length
        return `Selected ${n} table${n === 1 ? '' : 's'}`
      }
      if (Array.isArray(parsed.answers)) {
        const n = parsed.answers.reduce((acc, a) => acc + (a.selected?.length || 0), 0)
        return `Confirmed scope (${n} selection${n === 1 ? '' : 's'})`
      }
    } catch { /* plain text that happens to start with { */ }
  }
  return text
}

function newClientId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseAutoAgentOptions {
  project: string
  onComplete?: () => void
}

export function useAutoAgent({ project, onComplete }: UseAutoAgentOptions) {
  const [sessionId, setSessionId] = useState<string | null>(null)
  // The socket handler is memoised; this keeps it on the current project when
  // it attaches a resume to an out-of-credits refusal.
  const projectRef = useRef(project)
  projectRef.current = project
  const [wsStatus, setWsStatus] = useState<WsStatus>('disconnected')
  const [agentStatus, setAgentStatus] = useState<AgentStatus>('setup')
  const [todos, setTodos] = useState<AutoAgentTodo[]>([])
  const [phases, setPhases] = useState<AutoAgentPhase[]>([])
  const [logs, setLogs] = useState<AutoAgentLogEntry[]>([])
  const [replies, setReplies] = useState<AutoAgentReply[]>([])
  const [question, setQuestion] = useState<AutoAgentQuestion | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasExistingState, setHasExistingState] = useState(false)
  const [streamingStepOutput, setStreamingStepOutput] = useState<string>('')
  const [activeChangeStep, setActiveChangeStep] = useState<string | null>(null)
  const [currentTask, setCurrentTask] = useState<TaskInfo | null>(null)
  const [turnUsages, setTurnUsages] = useState<AutoAgentTurnUsage[]>([])
  const [plans, setPlans] = useState<AutoAgentPlanSummary[]>([])
  const [activePlanId, setActivePlanId] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const logIdRef = useRef(0)
  const replyIdRef = useRef(0)
  const turnUsageIdRef = useRef(0)
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const fatalRef = useRef(false)
  // Identifies this tab on the shared session (see WsEvent.client_id).
  const clientIdRef = useRef<string>(newClientId())
  // Highest event seq applied — reconnects attach with ?after_seq= so the
  // server replays only what this tab missed, and replays are de-duplicated.
  const lastSeqRef = useRef(0)
  // The session the transcript was loaded for, so a re-init of the SAME
  // session doesn't append the history twice.
  const hydratedSidRef = useRef<string | null>(null)
  // The current session id, readable from the socket handlers.
  const sessionIdRef = useRef<string | null>(null)
  sessionIdRef.current = sessionId
  // hydrateTranscript / initSession are declared after the socket handlers
  // that need them (replay_gap, a session that ended) — reached via refs.
  const hydrateRef = useRef<((sid: string, replace?: boolean) => Promise<void>) | null>(null)
  const initSessionRef = useRef<((opts?: { replace?: boolean }) => Promise<unknown>) | null>(null)
  // One automatic re-join per lost session, so a server that keeps refusing
  // cannot loop us.
  const rejoinedRef = useRef(false)
  const authRetryRef = useRef(false)
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const addLog = useCallback((
    message: string,
    level: AutoAgentLogEntry['level'] = 'info',
    meta?: Pick<AutoAgentLogEntry, 'elapsedS' | 'tokens' | 'costUsd'>,
  ) => {
    setLogs((prev) => [
      ...prev,
      { id: logIdRef.current++, message, level, timestamp: new Date(), ...meta },
    ])
  }, [])

  const addReply = useCallback((
    content: string,
    role: AutoAgentReply['role'],
    usage?: Pick<AutoAgentReply, 'durationMs' | 'inputTokens' | 'outputTokens' | 'costUsd'>,
  ) => {
    setReplies((prev) => [
      ...prev,
      { id: replyIdRef.current++, content, role, timestamp: new Date(), ...usage },
    ])
  }, [])

  // Tell any open live preview (EndUserAppView) to reload its metadata so the
  // app visibly fills in as the agent finishes each task — the "watch it come
  // to life" experience in Builder Mode. EndUserAppView already listens for
  // this event and preserves the user's expanded/selected state across reloads.
  const notifyPreview = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('twasta:agent-files-changed', { detail: { project, files: [] } }),
    )
  }, [project])

  // -----------------------------------------------------------------------
  // WebSocket message handling
  // -----------------------------------------------------------------------
  const handleMessage = useCallback((event: WsEvent) => {
    if (typeof event.seq === 'number') {
      if (event.seq <= lastSeqRef.current) return
      lastSeqRef.current = event.seq
    }
    switch (event.type) {
      // Another device on this build (the phone, another tab) typed something.
      case 'user_message':
        if (event.client_id && event.client_id === clientIdRef.current) break
        if (event.content || event.attachments?.length) {
          addReply(
            event.content
              || `📎 ${event.attachments!.length} attachment${event.attachments!.length === 1 ? '' : 's'}`,
            'user',
          )
        }
        break

      // A question was answered — possibly on another device. Retire the card
      // here too so nobody answers it twice.
      case 'answer_given': {
        setQuestion(prev =>
          prev && (!event.question_id || prev.question_id === event.question_id) ? null : prev,
        )
        if (event.question_id) {
          const qid = event.question_id
          setReplies((prev) => upsertAskReply(prev, { question_id: qid, state: 'answered' }, () => prev[0]))
        }
        if (event.client_id && event.client_id === clientIdRef.current) break
        const shown = summarizeAnswer(event.answer)
        if (shown) addReply(shown, 'user')
        break
      }

      // This tab was away longer than the server's replay buffer: reload the
      // conversation from the transcript rather than show it with a hole.
      case 'replay_gap':
        hydratedSidRef.current = null
        // Reload what was missed instead of leaving a silent hole (or an
        // empty dock) until the next full re-init.
        if (sessionIdRef.current) {
          void hydrateRef.current?.(sessionIdRef.current, true)
        }
        break

      case 'state':
        if (event.todos) {
          // Server may send raw todo objects — ensure running todos are
          // reset to pending when paused/awaiting-review/cancelled so the
          // spinner stops.
          const status = event.status as AgentStatus | undefined
          const idle = status === 'paused' || status === 'awaiting_review'
          const todosNormalized = event.todos.map((t) => ({
            ...t,
            status: (idle && t.status === 'running' ? 'pending' : t.status) as TodoStatus,
          }))
          setTodos(todosNormalized)
        }
        if (event.phases) setPhases(event.phases)
        if (event.status) setAgentStatus(event.status)
        break

      // The agent finished the blueprint (prototype) or a phase and is waiting
      // for the user's Continue / Build task by task / Complete decision.
      case 'phase_plan':
        if (event.phases) setPhases(event.phases)
        if (event.todos) setTodos(event.todos)
        setAgentStatus((event.status as AgentStatus) || 'awaiting_review')
        // A phase-boundary pause (current_phase set) is a milestone worth
        // keeping in history; a plain re-emit stays transient.
        if (event.summary) addLog(event.summary, event.current_phase ? 'success' : 'info')
        break

      // The agent re-derived the plan after a manual change while paused.
      case 'plan_updated':
        if (event.changes && event.changes.length) {
          addLog('Plan updated: ' + event.changes.join(', '), 'info')
        }
        break

      // The unified plan list (dock switcher).
      case 'plans':
        if (event.plans) setPlans(event.plans)
        if (event.active_plan_id !== undefined) setActivePlanId(event.active_plan_id ?? null)
        break

      // A different plan became active (select/resume); the server follows this
      // with a `state` event carrying that plan's todos/phases.
      case 'plan_selected':
        if (event.plan_id !== undefined) setActivePlanId(event.plan_id ?? null)
        break

      case 'todo_update':
        if (event.todo_id) {
          const now = Date.now() / 1000
          setTodos((prev) =>
            prev.map((t) => {
              if (t.id !== event.todo_id) return t
              const newStatus = (event.status as TodoStatus) || t.status
              const enteringRunning = newStatus === 'running' && t.status !== 'running'
              return {
                ...t,
                status: newStatus,
                // The backend includes `error` ONLY on an 'error' status; for
                // running/completed/skipped it sends None (or omits it). Using
                // `event.error ?? t.error` would make a stale error from a
                // previous failed attempt stick forever — it never cleared on
                // regeneration. Carry an error only while the status is
                // actually 'error'; any other status clears it.
                error: newStatus === 'error' ? (event.error ?? t.error ?? null) : null,
                review_iters: event.review_iters ?? t.review_iters,
                stage: event.stage ?? t.stage,
                // Reset timing when a todo (re)enters running so the displayed
                // duration reflects THIS run, not the first attempt. Timing is
                // tracked on the CLIENT clock (now) for both transitions so the
                // elapsed = completed - started never mixes clock sources; the
                // server values are only a last-resort fallback.
                started_at: enteringRunning ? now : (t.started_at ?? event.started_at ?? null),
                completed_at: enteringRunning
                  ? null
                  : (['completed', 'error', 'skipped'].includes(newStatus)
                      ? now
                      : (t.completed_at ?? event.completed_at ?? null)),
                input_tokens: event.input_tokens ?? t.input_tokens,
                output_tokens: event.output_tokens ?? t.output_tokens,
                cost_usd: event.cost_usd ?? t.cost_usd,
              }
            })
          )
          // Track active change step for streaming output
          if (event.todo_id.startsWith('change_')) {
            if (event.status === 'running') {
              setActiveChangeStep(event.todo_id)
              setStreamingStepOutput('')
            }
            if (['completed', 'error', 'skipped'].includes(event.status as string)) {
              setActiveChangeStep(null)
              setStreamingStepOutput('')
            }
          }
          // A finished task may have added/changed a screen — refresh any open
          // preview so it appears immediately, mid-generation.
          if (event.status === 'completed') notifyPreview()
        }
        break

      case 'task_info':
        setCurrentTask({
          task_type: event.task_type || '',
          effort: event.effort || '',
          model: event.model || '',
        })
        break

      case 'change_step_output':
        if (event.content) {
          setStreamingStepOutput(prev => prev + event.content)
        }
        break

      case 'log':
        if (event.message) {
          addLog(event.message, event.level || 'info', {
            elapsedS: event.elapsed_s ?? null,
            tokens: event.tokens ?? null,
            costUsd: event.cost_usd ?? null,
          })
        }
        break

      case 'question':
        if (event.question_id && event.question) {
          const qid = event.question_id
          const text = event.question
          setQuestion({
            question_id: qid,
            question: text,
            options: event.options || [],
            ...(event.scope ? { scope: event.scope } : {}),
            ...(event.table_picker ? { table_picker: event.table_picker } : {}),
            ...(event.connection_form ? { connection_form: true } : {}),
            ...(event.form_kind === 'ioflow_connect'
              ? { form_kind: 'ioflow_connect' as const, ...(event.app_id ? { app_id: event.app_id } : {}) }
              : {}),
            ...(event.parked ? { parked: true } : {}),
            ...(event.asked_at ? { asked_at: event.asked_at } : {}),
          })
          // Keep the question in the conversation, once.
          setReplies((prev) => upsertAskReply(prev, { question_id: qid, state: 'open' }, () => ({
            id: replyIdRef.current++,
            content: text,
            role: 'agent',
            timestamp: event.asked_at ? new Date(event.asked_at * 1000) : new Date(),
            ask: { question_id: qid, state: 'open' },
          })))
        }
        break

      // The question is no longer open — the user sent a different
      // instruction (or pressed Stop). Retire the card; the conversation keeps
      // a note of it. 'question_expired' is the old name (servers before
      // questions were parked) and is handled the same way.
      case 'question_withdrawn':
      case 'question_expired':
        setQuestion(prev =>
          prev && (!event.question_id || prev.question_id === event.question_id) ? null : prev,
        )
        if (event.question_id) {
          const qid = event.question_id
          setReplies((prev) => upsertAskReply(prev, { question_id: qid, state: 'withdrawn' }, () => prev[0]))
        }
        break

      case 'agent_reply':
        if (event.content) {
          addReply(event.content, 'agent', {
            durationMs: event.duration_ms ?? null,
            inputTokens: event.input_tokens ?? null,
            outputTokens: event.output_tokens ?? null,
            costUsd: event.cost_usd ?? null,
          })
        }
        break

      // End-of-turn resource summary (time + tokens for the whole Ask-AI turn).
      case 'turn_usage':
        setTurnUsages((prev) => [
          ...prev,
          {
            id: turnUsageIdRef.current++,
            durationMs: event.duration_ms ?? 0,
            inputTokens: event.input_tokens ?? 0,
            outputTokens: event.output_tokens ?? 0,
            costUsd: event.cost_usd ?? null,
            calls: event.calls ?? 0,
            timestamp: new Date(),
          },
        ])
        break

      case 'complete':
        // An agent turn is the moment credits actually move, so pull the new
        // balance rather than waiting up to 60s for the menu's poll.
        refreshCredits()
        setAgentStatus('completed')
        setCurrentTask(null)
        setQuestion(null)
        addLog('All generation steps completed!', 'info')
        notifyPreview()
        onCompleteRef.current?.()
        break

      case 'cancelled':
        setAgentStatus('paused')
        setCurrentTask(null)
        // Reset running todos to pending so spinners stop
        setTodos((prev) => prev.map((t) =>
          t.status === 'running' ? { ...t, status: 'pending' as TodoStatus, started_at: null } : t
        ))
        addLog('Generation stopped.', 'info')
        break

      case 'ai_access_error': {
        // Platform allowance exhausted (or no usable key): pause the build and
        // surface the graceful upgrade / add-your-own-key modal. Pausing (not
        // erroring) lets the user resume the build after upgrading/adding a key.
        setAgentStatus('paused')
        setCurrentTask(null)
        setTodos((prev) => prev.map((t) =>
          t.status === 'running' ? { ...t, status: 'pending' as TodoStatus, started_at: null } : t
        ))
        addLog(event.detail || 'Out of AI credits — the build is paused.', 'warning')
        import('@/store/aiAccessStore').then(
          ({ reportAiAccessError, pickAiAccessFields }) => {
            // One shared picker: the refusal carries more than a plan and a
            // token pair (which cap tripped, a wallet balance), and copying
            // fields by hand here is what silently dropped them. `resend`
            // resumes the paused build once the user has fixed it.
            reportAiAccessError({
              ...pickAiAccessFields(event as unknown as Record<string, unknown>),
              resend: { project: projectRef.current, kind: 'resume' },
            })
          })
        break
      }

      case 'error':
        setError(event.message || event.detail || 'Unknown error')
        addLog(event.message || event.detail || 'Unknown error', 'error')
        break

      default:
        break
    }
  }, [addLog, addReply, notifyPreview])

  // -----------------------------------------------------------------------
  // Connect WebSocket
  // -----------------------------------------------------------------------
  const connect = useCallback((sid: string) => {
    if (fatalRef.current) return
    // One live socket max: supersede any scheduled reconnect and detach the
    // previous socket's handlers so its late close can't clobber this one.
    if (reconnectRef.current) {
      clearTimeout(reconnectRef.current)
      reconnectRef.current = null
    }
    const prev = wsRef.current
    if (prev) {
      prev.onopen = null
      prev.onmessage = null
      prev.onerror = null
      prev.onclose = null
      try { prev.close() } catch { /* already closed */ }
    }
    const afterSeq = lastSeqRef.current
    const url = `${WS_BASE()}/api/autoagent/ws/${sid}`
      + (afterSeq > 0 ? `?after_seq=${afterSeq}` : '')
    setWsStatus('connecting')
    const ws = new WebSocket(url)
    wsRef.current = ws

    ws.onopen = () => {
      if (wsRef.current !== ws) return
      setWsStatus('connected')
      setError(null)
      rejoinedRef.current = false
      reconnectAttemptsRef.current = 0
      authRetryRef.current = false
      // Fetch current state on connect
      ws.send(JSON.stringify({ type: 'get_state' }))
    }

    ws.onmessage = (e: MessageEvent) => {
      try {
        handleMessage(JSON.parse(e.data as string) as WsEvent)
      } catch { /* ignore */ }
    }

    ws.onerror = () => {
      if (wsRef.current === ws) setWsStatus('error')
    }

    ws.onclose = (e: CloseEvent) => {
      if (wsRef.current !== ws) return
      wsRef.current = null
      if (fatalRef.current) { setWsStatus('error'); return }
      // 4401 = the login cookie expired while the socket was down. Any REST
      // call through the api client refreshes it on 401; then reconnect once.
      if (e.code === 4401 && !authRetryRef.current) {
        authRetryRef.current = true
        setWsStatus('connecting')
        getActiveAgentSession(project)
          .then(() => connect(sid))
          .catch(() => {
            fatalRef.current = true
            setWsStatus('error')
            setError('Your login has expired. Sign in again to continue.')
          })
        return
      }
      // 4004 = the session ended (evicted, or the backend restarted). The
      // conversation, the plans and any question still owed an answer are on
      // disk — join a fresh session once instead of declaring the dock dead.
      if (e.code === 4004 && !rejoinedRef.current && initSessionRef.current) {
        rejoinedRef.current = true
        setWsStatus('connecting')
        hydratedSidRef.current = null
        lastSeqRef.current = 0
        void initSessionRef.current({ replace: true })
        return
      }
      if (e.code >= 4000 && e.code < 5000) {
        fatalRef.current = true
        setWsStatus('error')
        setError(e.reason || 'Connection rejected')
        return
      }
      // Retry forever with capped backoff — a backend restart can outlast any
      // fixed attempt count. After repeated failures surface 'error' so sends
      // fail fast (and the dock re-establishes the session on the next user
      // action) while retries keep going in the background.
      const attempt = reconnectAttemptsRef.current++
      setWsStatus(attempt >= 5 ? 'error' : 'disconnected')
      if (attempt === 5) {
        setError('Connection to the agent lost — retrying in the background.')
      }
      const delay = Math.min(1000 * 2 ** Math.min(attempt, 4), 16000)
      reconnectRef.current = setTimeout(() => connect(sid), delay)
    }
  }, [handleMessage, project])

  // -----------------------------------------------------------------------
  // Load the persisted conversation for this project (shared with the phone
  // and other tabs) before attaching, and work out which events of the live
  // session it already covers so the socket replays only the rest.
  // -----------------------------------------------------------------------
  const hydrateTranscript = useCallback(async (sid: string, replace: boolean = false) => {
    if (hydratedSidRef.current === sid && !replace) return
    let entries: AutoAgentTranscriptEntry[] = []
    try {
      const t = await getAutoAgentTranscript(project)
      entries = t.entries || []
    } catch (err) {
      // History is a convenience — a failed load must never block the build.
      console.warn('[useAutoAgent] transcript load failed', err)
      return
    }
    hydratedSidRef.current = sid
    const restoredReplies: AutoAgentReply[] = []
    const restoredLogs: AutoAgentLogEntry[] = []
    let maxSeq = 0
    for (const entry of entries) {
      const ev = entry.event as WsEvent
      const at = new Date((entry.ts || 0) * 1000)
      if (entry.sid === sid && entry.seq > maxSeq) maxSeq = entry.seq
      switch (ev.type) {
        case 'user_message': {
          const text = ev.content
            || (ev.attachments?.length ? `📎 ${ev.attachments.length} attachment${ev.attachments.length === 1 ? '' : 's'}` : '')
          if (text) restoredReplies.push({ id: replyIdRef.current++, content: text, role: 'user', timestamp: at })
          break
        }
        case 'answer_given': {
          if (ev.question_id) {
            const i = restoredReplies.findIndex((r) => r.ask?.question_id === ev.question_id)
            if (i >= 0) restoredReplies[i] = { ...restoredReplies[i], ask: { question_id: ev.question_id, state: 'answered' } }
          }
          const text = summarizeAnswer(ev.answer)
          if (text) restoredReplies.push({ id: replyIdRef.current++, content: text, role: 'user', timestamp: at })
          break
        }
        // The questions the agent asked. Whether one is still open is the
        // server's call (get_state re-sends it) — here it is history only.
        case 'question':
          if (ev.question_id && ev.question
              && !restoredReplies.some((r) => r.ask?.question_id === ev.question_id)) {
            restoredReplies.push({
              id: replyIdRef.current++, content: ev.question, role: 'agent', timestamp: at,
              ask: { question_id: ev.question_id, state: 'open' },
            })
          }
          break
        case 'question_withdrawn': {
          const i = restoredReplies.findIndex((r) => r.ask?.question_id === ev.question_id)
          if (i >= 0 && ev.question_id) {
            restoredReplies[i] = { ...restoredReplies[i], ask: { question_id: ev.question_id, state: 'withdrawn' } }
          }
          break
        }
        case 'agent_reply':
          if (ev.content) {
            restoredReplies.push({
              id: replyIdRef.current++, content: ev.content, role: 'agent', timestamp: at,
              durationMs: ev.duration_ms ?? null, inputTokens: ev.input_tokens ?? null,
              outputTokens: ev.output_tokens ?? null, costUsd: ev.cost_usd ?? null,
            })
          }
          break
        case 'phase_plan':
          if (ev.summary && ev.current_phase) {
            restoredLogs.push({ id: logIdRef.current++, message: ev.summary, level: 'success', timestamp: at })
          }
          break
        case 'error':
          if (ev.message) {
            restoredLogs.push({ id: logIdRef.current++, message: ev.message, level: 'error', timestamp: at })
          }
          break
        default:
          break
      }
    }
    if (replace) {
      // A reload of what this tab already shows (a replay gap, a re-joined
      // session): the transcript IS the conversation — never append it twice.
      setReplies(restoredReplies)
    } else {
      if (restoredReplies.length) setReplies(prev => [...restoredReplies, ...prev])
      if (restoredLogs.length) setLogs(prev => [...restoredLogs, ...prev])
    }
    if (maxSeq > lastSeqRef.current) lastSeqRef.current = maxSeq
  }, [project])
  hydrateRef.current = hydrateTranscript

  // -----------------------------------------------------------------------
  // Create session (or reconnect to an active one)
  // -----------------------------------------------------------------------
  const initSession = useCallback(async (
    opts?: { replace?: boolean },
  ): Promise<{ sessionId: string; reconnected: boolean } | null> => {
    const replace = !!opts?.replace
    try {
      setError(null)
      fatalRef.current = false

      // Check if there's already an active session for this project
      const active = await getActiveAgentSession(project)
      if (active.active && active.session_id) {
        const sid = active.session_id
        setSessionId(sid)
        setHasExistingState(true)
        if (sid !== hydratedSidRef.current) lastSeqRef.current = 0
        await hydrateTranscript(sid, replace)
        connect(sid)
        await new Promise<void>((r) => setTimeout(r, 500))
        return { sessionId: sid, reconnected: true }
      }

      // No active session — create a new one
      const resp = await createAutoAgentSession(project)
      const sid = resp.session_id
      setSessionId(sid)
      setHasExistingState(resp.has_existing_state)
      if (sid !== hydratedSidRef.current) lastSeqRef.current = 0
      await hydrateTranscript(sid, replace)
      connect(sid)
      await new Promise<void>((r) => setTimeout(r, 500))
      return { sessionId: sid, reconnected: false }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || 'Failed to create agent session'
      setError(msg)
      return null
    }
  }, [project, connect, hydrateTranscript])
  initSessionRef.current = initSession

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  // Returns false when the socket is down so callers surface the failure —
  // a dropped frame must never look like a delivered one.
  const send = useCallback((msg: object): boolean => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg))
      return true
    }
    return false
  }, [])

  // A user-visible transcript entry for a frame that could not be delivered.
  const reportSendFailure = useCallback((what: string = 'That request') => {
    addLog(
      `${what} was not delivered — the agent connection is down and reconnecting. `
      + 'Try again in a moment.',
      'error',
    )
  }, [addLog])

  // mode: 'step' = the deterministic FRS -> review -> app pipeline (restores the
  // old step-by-step flow; analyses a referenced source up front). 'build' =
  // the fully agentic, multi-agent Ask-AI orchestrator that decides the steps.
  const start = useCallback(async (
    description: string,
    profileId?: number | null,
    mode: 'step' | 'build' = 'step',
    // Files attached on the composer that kicked off this build (e.g. a PDF
    // spec attached on the Builder hub). Documents are extracted to text
    // server-side; see backend/app/services/attachments.py.
    attachments?: WireAttachment[],
  ) => {
    let sid = sessionId
    if (!sid || wsRef.current?.readyState !== WebSocket.OPEN) {
      const result = await initSession()
      sid = result?.sessionId ?? null
    }
    if (!sid) return
    // Wait for WS to be open
    await new Promise<void>((resolve) => {
      const check = () => {
        if (wsRef.current?.readyState === WebSocket.OPEN) resolve()
        else setTimeout(check, 100)
      }
      check()
      setTimeout(resolve, 3000)
    })
    setError(null)
    // Deliver before flipping the UI to 'running' — an undelivered start on a
    // dead socket must not leave a phantom spinner behind.
    if (!send({
      type: 'start', description, profile_id: profileId ?? null, mode,
      client_id: clientIdRef.current,
      ...(attachments?.length ? { attachments } : {}),
    })) {
      reportSendFailure('The build request')
      return
    }
    setAgentStatus('running')
  }, [sessionId, initSession, send, reportSendFailure])

  const pause = useCallback(() => {
    if (!send({ type: 'pause' })) {
      reportSendFailure('The pause request')
      return
    }
    // Optimistic update so UI responds immediately
    setAgentStatus('paused')
    setTodos((prev) => prev.map((t) =>
      t.status === 'running' ? { ...t, status: 'pending' as TodoStatus, started_at: null } : t
    ))
    addLog('Pausing…', 'info')
  }, [send, addLog, reportSendFailure])
  const resume = useCallback(() => {
    if (!send({ type: 'resume' })) {
      reportSendFailure('The resume request')
      return
    }
    setAgentStatus('running')
  }, [send, reportSendFailure])

  // A build paused for credits resumes by itself once the user has bought
  // credits / set up their AI from the out-of-credits card — as soon as the
  // (re)connected socket has reported the paused state.
  useEffect(() => {
    if (wsStatus !== 'connected' || agentStatus !== 'paused') return
    if (takePendingResend(project, ['resume'])) resume()
  }, [wsStatus, agentStatus, project, resume])
  const cancel = useCallback(() => {
    if (!send({ type: 'cancel' })) {
      reportSendFailure('The stop request')
      return
    }
    setAgentStatus('paused')
    setTodos((prev) => prev.map((t) =>
      t.status === 'running' ? { ...t, status: 'pending' as TodoStatus, started_at: null } : t
    ))
    addLog('Stopping…', 'info')
  }, [send, addLog, reportSendFailure])

  // From a review pause: proceed by the next phase, the next single object, or
  // everything remaining (no further pauses).
  const continueScope = useCallback((scope: RunScope) => {
    if (!send({ type: 'continue', scope })) {
      reportSendFailure('The continue request')
      return
    }
    setAgentStatus('running')
  }, [send, reportSendFailure])
  const continueNextPhase = useCallback(() => continueScope('phase'), [continueScope])
  const buildTaskByTask = useCallback(() => continueScope('task'), [continueScope])
  const completeEverything = useCallback(() => continueScope('all'), [continueScope])

  // -----------------------------------------------------------------------
  // Multi-plan controls (the dock plan switcher)
  // -----------------------------------------------------------------------
  const refreshPlans = useCallback(() => send({ type: 'list_plans' }), [send])
  const selectPlan = useCallback((planId: string) => {
    if (!send({ type: 'select_plan', plan_id: planId })) {
      reportSendFailure('The plan selection')
    }
  }, [send, reportSendFailure])
  const resumePlan = useCallback((planId: string, scope: RunScope = 'phase') => {
    if (!send({ type: 'resume_plan', plan_id: planId, scope })) {
      reportSendFailure('The resume request')
      return
    }
    setAgentStatus('running')
  }, [send, reportSendFailure])
  // ``force`` skips the checkpoint handshake and stops the run outright — the
  // dock sends it on a second pause press, so a step that never reaches a
  // checkpoint can't leave the plan stamped "running" (and so undeletable).
  const pausePlan = useCallback((planId: string, force = false) => {
    if (!send({ type: 'pause_plan', plan_id: planId, force })) {
      reportSendFailure('The pause request')
    }
  }, [send, reportSendFailure])
  const deletePlan = useCallback((planId: string) => {
    if (!send({ type: 'delete_plan', plan_id: planId })) {
      reportSendFailure('The delete request')
    }
  }, [send, reportSendFailure])

  // Drop the tracker state belonging to a plan that no longer exists. The
  // progress footer (task list, Continue / Build task by task / Complete
  // everything) is driven by `todos`, NOT by the plans list — so deleting the
  // displayed plan left its card hanging until a page refresh. Callers pass the
  // deleted plan's id and this only clears when that plan is the one on screen.
  const forgetPlanState = useCallback((planId: string) => {
    setActivePlanId((cur) => {
      if (cur !== null && cur !== planId) return cur
      setTodos([])
      setPhases([])
      setAgentStatus('setup')
      setHasExistingState(false)
      return null
    })
    setPlans((prev) => prev.filter((p) => p.id !== planId))
  }, [])
  const newPlan = useCallback((description: string) => {
    if (!description.trim()) return
    if (!send({ type: 'new_plan', description, client_id: clientIdRef.current })) {
      reportSendFailure('The new-plan request')
      return
    }
    setAgentStatus('running')
  }, [send, reportSendFailure])

  const answerQuestion = useCallback((
    questionId: string, answer: string, connectionId?: string,
  ) => {
    // Deliver first — on a dead socket keep the question card up (and say so)
    // instead of retiring it while the answer went nowhere.
    // ``connection_id`` (connection-form answers) binds the run to that EXACT
    // registered connection — the prose answer is for the transcript only.
    if (!send({
      type: 'answer',
      question_id: questionId,
      answer,
      client_id: clientIdRef.current,
      ...(connectionId ? { connection_id: connectionId } : {}),
    })) {
      reportSendFailure('Your answer')
      return
    }
    setQuestion(null)
    // An empty answer means "skip / proceed with defaults" — don't render an
    // empty user bubble for it.
    if (answer.trim()) addReply(answer, 'user')
  }, [send, addReply, reportSendFailure])

  // Answer a scope-confirmation question: the wire answer is the JSON
  // decisions object (the backend parses it into the FRS prompt), while the
  // chat transcript gets a short human-readable summary instead of raw JSON.
  const answerScopeQuestion = useCallback((questionId: string, decisions: import('@/types').ScopeDecisions) => {
    if (!send({
      type: 'answer', question_id: questionId, answer: JSON.stringify(decisions),
      client_id: clientIdRef.current,
    })) {
      reportSendFailure('Your scope confirmation')
      return
    }
    setQuestion(null)
    const nSelected = decisions.answers.reduce((n, a) => n + a.selected.length, 0)
    const nCustom = decisions.answers.reduce((n, a) => n + a.custom.length, 0)
    const parts = [`Confirmed scope (${nSelected} selection${nSelected === 1 ? '' : 's'}`]
    if (nCustom > 0) parts.push(`${nCustom} added point${nCustom === 1 ? '' : 's'}`)
    let summary = parts.join(', ') + ')'
    if (decisions.additional_notes) summary += ` — ${decisions.additional_notes}`
    addReply(summary, 'user')
  }, [send, addReply, reportSendFailure])

  // Answer the large-source table-picker question: the wire answer is a JSON
  // {"tables": [...], "include_related": bool} object (with the flag on, the
  // backend expands the seeds into the related set; off, the selection IS the
  // scope), while the chat transcript gets a readable summary. An empty
  // selection means "skip — explore the whole source".
  const answerTablePicker = useCallback((
    questionId: string, tables: string[], includeRelated: boolean = true,
  ) => {
    if (!send({
      type: 'answer',
      question_id: questionId,
      client_id: clientIdRef.current,
      answer: tables.length > 0
        ? JSON.stringify({ tables, include_related: includeRelated })
        : '',
    })) {
      reportSendFailure('Your table selection')
      return
    }
    setQuestion(null)
    if (tables.length > 0) {
      const shown = tables.slice(0, 8).join(', ')
      addReply(
        `Selected ${tables.length} table${tables.length === 1 ? '' : 's'}: ${shown}`
        + (tables.length > 8 ? `, +${tables.length - 8} more` : '')
        + (includeRelated ? ' (+ related tables)' : ' (exactly these — no related expansion)'),
        'user',
      )
    }
  }, [send, addReply, reportSendFailure])

  const sendMessage = useCallback((
    content: string,
    model?: string,
    intentHint?: string,
    mode?: string,
    openObject?: { obj_name: string; obj_type: string; title: string } | null,
    attachments?: WireAttachment[],
  ) => {
    const hasAttachments = !!(attachments && attachments.length)
    if (!content.trim() && !hasAttachments) return false
    // Display only the user's typed text; the open-object context is sent to
    // the backend separately so the chat bubble stays clean. For an
    // attachment-only message, show a short placeholder so the bubble isn't empty.
    const display = content.trim()
      || (hasAttachments
        ? `📎 ${attachments!.length} attachment${attachments!.length === 1 ? '' : 's'}`
        : content)
    // Echo BEFORE attempting delivery — what the user typed must always show
    // in the transcript, even when the connection turns out to be dead.
    addReply(display, 'user')
    const delivered = send({
      type: 'message',
      content,
      client_id: clientIdRef.current,
      ...(model ? { model } : {}),
      ...(intentHint ? { intent_hint: intentHint } : {}),
      ...(mode ? { mode } : {}),
      ...(openObject ? { open_object: openObject } : {}),
      ...(hasAttachments ? { attachments } : {}),
    })
    if (!delivered) {
      addLog(
        'This message was not delivered — the agent connection is down and '
        + 'reconnecting. Press ↑ in the message box to recall it and resend.',
        'error',
      )
      return false
    }
    setAgentStatus('running')
    return true
  }, [send, addReply, addLog])

  const clearLogs = useCallback(() => {
    setLogs([])
    setReplies([])
    setTurnUsages([])
    setTodos([])
    setPhases([])
    setAgentStatus('setup')
    setHasExistingState(false)
    setError(null)
    // Plans are durable server-side; keep them so the switcher still lists them.
  }, [])

  // -----------------------------------------------------------------------
  // Sync agent status to global store (for sidebar indicator)
  // -----------------------------------------------------------------------
  useEffect(() => {
    const running = agentStatus === 'running'
    useAgentStore.getState().setRunning(running, running ? project : null)
  }, [agentStatus, project])

  // -----------------------------------------------------------------------
  // Cleanup
  // -----------------------------------------------------------------------
  useEffect(() => {
    return () => {
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      fatalRef.current = true
      wsRef.current?.close()
      wsRef.current = null
      // Don't reset agentStore here — the backend task may still be running
    }
  }, [])

  return {
    sessionId,
    wsStatus,
    agentStatus,
    awaitingReview: agentStatus === 'awaiting_review',
    todos,
    phases,
    logs,
    replies,
    question,
    error,
    hasExistingState,
    streamingStepOutput,
    activeChangeStep,
    currentTask,
    turnUsages,
    plans,
    activePlanId,
    initSession,
    start,
    pause,
    resume,
    cancel,
    continueNextPhase,
    buildTaskByTask,
    completeEverything,
    answerQuestion,
    answerScopeQuestion,
    answerTablePicker,
    sendMessage,
    clearLogs,
    refreshPlans,
    selectPlan,
    resumePlan,
    pausePlan,
    deletePlan,
    forgetPlanState,
    newPlan,
  }
}
